import { useState } from 'react';
import { ChevronDown, Building2, Check } from 'lucide-react';
import type { ApiOrganization } from '@/lib/api-client';
import { useSwitchOrg } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

export function OrgSwitcher({
  orgs,
  activeOrgId,
}: {
  orgs: ApiOrganization[];
  activeOrgId: string;
}) {
  const [open, setOpen] = useState(false);
  const switchOrg = useSwitchOrg();
  const active = orgs.find((o) => o.id === activeOrgId);
  if (!active) return null;

  const onSelect = async (id: string) => {
    if (id === activeOrgId) {
      setOpen(false);
      return;
    }
    await switchOrg.mutateAsync(id);
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-2 rounded-md border bg-background hover:bg-accent text-sm font-medium"
      >
        <Building2 className="w-4 h-4" />
        <span>{active.name}</span>
        <ChevronDown className="w-4 h-4 opacity-60" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1 w-64 z-50 rounded-md border bg-popover shadow-lg">
            <div className="py-1 max-h-80 overflow-auto">
              {orgs.map((org) => (
                <button
                  key={org.id}
                  type="button"
                  onClick={() => onSelect(org.id)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-accent flex items-center justify-between gap-2"
                >
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium">{org.name}</div>
                    <div className="text-xs text-muted-foreground capitalize">{org.role}</div>
                  </div>
                  {org.id === activeOrgId && <Check className="w-4 h-4 text-primary" />}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
      {switchOrg.isPending && (
        <div className={cn('absolute inset-0 bg-background/50 rounded-md')} />
      )}
    </div>
  );
}
